import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface SyncRow {
  readonly syncId: string;
  readonly syncCode: string;
  readonly masterType: string;
  readonly direction: string;
  readonly recordsTotal: number;
  readonly recordsSucceeded: number;
  readonly recordsFailed: number;
  readonly status: string;
}

const MASTER_TYPES = ['', 'VENDOR', 'USER', 'TERRITORY', 'CAMPAIGN', 'COST_CENTER', 'FINANCE', 'REFERENCE'];
const START_TYPES = MASTER_TYPES.filter(Boolean);
const DIRECTIONS = ['INBOUND', 'OUTBOUND', 'BIDIRECTIONAL'];

/** M29 — Master-Data Sync Dashboard (§33 / §23). History of master-data
 * synchronisation runs across systems with their direction, record counts and
 * outcome (BR-06: master-data consistency across integrated systems). Operators
 * can manually start a sync run via the guarded /v1/eip/sync API; per-record
 * progress/complete/fail transitions are driven by the sync worker. */
export function M29SyncDashboardPage(): ReactNode {
  const { token } = useAuth();
  const { t: tr } = useTranslation();
  const [rows, setRows] = useState<SyncRow[]>([]);
  const [masterType, setMasterType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // start form
  const [newMasterType, setNewMasterType] = useState('VENDOR');
  const [direction, setDirection] = useState('INBOUND');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const qs = masterType ? `?masterType=${masterType}` : '';
    apiRequest<{ data: SyncRow[] }>(`/v1/eip/sync${qs}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, masterType, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m29.sync.notAuthorised', { verb, perm })
      : (err as Error).message;

  const startSync = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      const r = await apiRequest<{ data: SyncRow }>(`/v1/eip/sync`, {
        method: 'POST',
        body: { masterType: newMasterType, direction },
        token,
      });
      setNotice(tr('m29.sync.noticeStarted', { code: r.data.syncCode }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m29.sync.verbStart'), 'M29_SYNC_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const failed = rows.filter((s) => s.status === 'FAILED').length;
  const partial = rows.filter((s) => s.status === 'PARTIAL').length;

  return (
    <main aria-label="sync-dashboard">
      <h1>{tr('m29.sync.title')}</h1>

      <section aria-label="start-sync">
        <h2>{tr('m29.sync.startHeading')}</h2>
        <form aria-label="sync-form" onSubmit={(e) => void startSync(e)}>
          <label>{tr('m29.sync.fieldMasterType')}
            <select aria-label="field-master-type" value={newMasterType} onChange={(e) => setNewMasterType(e.target.value)}>
              {START_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m29.sync.fieldDirection')}
            <select aria-label="field-direction" value={direction} onChange={(e) => setDirection(e.target.value)}>
              {DIRECTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-sync" disabled={busy}>{busy ? tr('m29.sync.starting') : tr('m29.sync.start')}</button>
        </form>
        {actionError && <p role="alert" aria-label="sync-error">{actionError}</p>}
        {notice && <p aria-label="sync-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m29.sync.filterMasterType')}
          <select aria-label="filter-master-type" value={masterType} onChange={(e) => setMasterType(e.target.value)}>
            {MASTER_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m29.sync.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m29.sync.loading')}</p> : (
        <>
          <p aria-label="sync-count">{tr('m29.sync.count', { count: rows.length })}</p>
          <p aria-label="sync-failed">{tr('m29.sync.failed', { count: failed })}</p>
          <p aria-label="sync-partial">{tr('m29.sync.partial', { count: partial })}</p>
          <table aria-label="sync-table">
            <thead><tr><th>{tr('m29.sync.colCode')}</th><th>{tr('m29.sync.colMaster')}</th><th>{tr('m29.sync.colDirection')}</th><th>{tr('m29.sync.colTotal')}</th><th>{tr('m29.sync.colSucceeded')}</th><th>{tr('m29.sync.colFailed')}</th><th>{tr('m29.sync.colStatus')}</th></tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.syncId}>
                  <td>{s.syncCode}</td><td>{s.masterType}</td><td>{s.direction}</td>
                  <td>{s.recordsTotal}</td><td>{s.recordsSucceeded}</td><td>{s.recordsFailed}</td><td>{s.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
